import { CONFIGURATOR_DATA } from './configuratorData';
import { useState, useRef } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { jsPDF } from 'jspdf';
import DeliveryOptions from './DeliveryOptions';

export default function PriceSummary({ config, totalPrice, basePrice, steps = CONFIGURATOR_DATA, onSubmit, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [submitted, setSubmitted] = useState(onSubmit ? false : true);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', postalCode: '' });
  const [delivery, setDelivery] = useState({ type: 'pickup', cost: 0 });

  const getNorwegianLabel = (key) => {
    const keyMap = {
      'cladding': 'KLEDNING',
      'ovenModel': 'BADSTUEOVN',
      'interiorPanels': 'INNVENDIGE PANEL',
      'floor': 'GULV',
      'lighting': 'BELYSNING OG LYD ANLEGG',
      'doors': 'DØRER',
      'windows': 'VINDU',
      'extras': 'EKSTRA'
    };
    return keyMap[key] || key;
  };

  const deliveryItems = delivery.cost > 0 ? [{ label: 'Leveringskost', option: delivery.type === 'delivery' ? 'Levering til adresse' : 'Hente selv', price: delivery.cost }] : [];
  
  const lineItems = steps.filter(s => s.id !== undefined).flatMap(section => {
    const displayLabel = getNorwegianLabel(section.id);
    const configValue = config[section.id];
    
    if (section.multiselect && Array.isArray(configValue)) {
      return configValue.map(id => {
        const selected = section.options.find(o => o.id === id);
        return { label: displayLabel, option: selected?.label, price: selected?.price || 0 };
      });
    } else {
      const selected = section.options.find(o => o.id === configValue);
      return [{ label: displayLabel, option: selected?.label, price: selected?.price || 0 }];
    }
  }).concat(deliveryItems);
  
  const finalTotalPrice = totalPrice + delivery.cost;

  const downloadPriceBreakdown = async () => {
    setDownloadLoading(true);
    try {
      const doc = new jsPDF({ format: 'a4' });
      const sauna = steps[0]?.label || 'Sauna Configuration';
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      
      // Background color
      doc.setFillColor(245, 244, 242);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Logo
      doc.setFont('Cormorant Garamond', 'normal');
      doc.setFontSize(20);
      doc.setTextColor(60, 60, 60);
      doc.text('LYKKE', margin, 15);
      
      // Header
      doc.setFont('Cormorant Garamond', 'normal');
      doc.setFontSize(16);
      doc.setTextColor(60, 60, 60);
      doc.text('PRISSAMMENDRAG', margin, 32);
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(`${finalTotalPrice.toLocaleString('nb-NO')} kr`, pageWidth - margin, 32, { align: 'right' });
      
      // Line separator
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 39, pageWidth - margin, 39);
      
      let y = 49;
      
      // Base price
      doc.setFont('Cormorant Garamond', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Grunnpris', margin, y);
      doc.setTextColor(0, 0, 0);
      doc.text(`${(basePrice || steps[0]?.basePrice || 0).toLocaleString('nb-NO')} kr`, pageWidth - margin, y, { align: 'right' });
      
      y += 12;
      
      // Configuration items
      lineItems.forEach((item, idx) => {
       doc.setFont('Cormorant Garamond', 'normal');
       doc.setFontSize(8);
       doc.setTextColor(120, 120, 120);
       doc.text(item.label, margin, y);

        doc.setFont('Cormorant Garamond', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.text(item.option || '', margin, y + 5);

        const priceColor = item.price > 0 ? [0, 0, 0] : [140, 140, 140];
        doc.setTextColor(...priceColor);
        doc.text(`${item.price > 0 ? '+' : ''}${item.price.toLocaleString('nb-NO')} kr`, pageWidth - margin, y + 5, { align: 'right' });

        y += 14;
      });
      
      y += 3;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);
      y += 10;
      
      // Total
      doc.setFont('Cormorant Garamond', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text('TOTALT ESTIMAT', margin, y);
      
      doc.setFont('Cormorant Garamond', 'normal');
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(`${finalTotalPrice.toLocaleString('nb-NO')} kr`, pageWidth - margin, y, { align: 'right' });
      
      doc.setFontSize(8);
      doc.setTextColor(140, 140, 140);
      doc.text('Fastpris inkl MVA 25%', pageWidth - margin, y + 6, { align: 'right' });
      
      const pdf = doc.output('arraybuffer');
      const blob = new Blob([pdf], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Prissammendrag_${new Date().getTime()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Feil ved nedlasting av PDF');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataObj = new FormData(e.target);
      const firstName = formDataObj.get('firstName');
      const email = formDataObj.get('email');
      const phone = formDataObj.get('phone');
      const postalCode = formDataObj.get('postalCode');
      const address = postalCode; // Use postal code as placeholder for address

      const updatedFormData = { firstName, email, phone, postalCode };
      setFormData(updatedFormData);

      // Build configurator details object with IDs for price lookup
      const configuratorDetails = {};
      steps.filter(s => !s.isSummary).forEach(step => {
        const configValue = config[step.id];
        if (configValue) {
          if (Array.isArray(configValue)) {
            // For multiselect: store objects with id and label
            configuratorDetails[step.label] = configValue.map(id => {
              const opt = step.options.find(o => o.id === id);
              return {
                id: id,
                label: opt?.label || id
              };
            });
          } else {
            // For single select: store object with id and label
            const opt = step.options.find(o => o.id === configValue);
            configuratorDetails[step.label] = {
              id: configValue,
              label: opt?.label || configValue
            };
          }
        }
      });

      // Build price map for email
      const priceMap = {};
      steps.filter(s => !s.isSummary).forEach(step => {
        const configValue = config[step.id];
        if (configValue) {
          if (Array.isArray(configValue)) {
            configValue.forEach(id => {
              const opt = step.options.find(o => o.id === id);
              if (opt) priceMap[id] = opt.price || 0;
            });
          } else {
            const opt = step.options.find(o => o.id === configValue);
            if (opt) priceMap[configValue] = opt.price || 0;
          }
        }
      });

      await base44.functions.invoke('submitSaunaConfiguratorLead', {
        firstName,
        email,
        phone,
        address,
        sauna: steps[0]?.label || 'Sauna Configuration',
        totalPrice: finalTotalPrice,
        deliveryType: delivery?.type || 'pickup',
        deliveryCost: delivery?.cost || 0,
        configuratorDetails,
        postalCode,
        priceMap
      });

      setFormData({ firstName, email, phone, postalCode });

      setSubmitted(true);
      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      console.error('Error submitting configuration:', error);
      alert('Det oppstod en feil przy sending. Vennligst prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={defaultOpen ? '' : 'mt-10 border-t border-border pt-8'}>
      {/* Toggle price breakdown */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 border border-border px-5 hover:bg-card transition-colors rounded-sm"
      >
        <span className="font-sans text-[11px] tracking-ultra uppercase text-foreground">
          Prissammendrag
        </span>
        <div className="flex items-center gap-4">
          <span className="font-serif text-lg text-foreground">
            {finalTotalPrice.toLocaleString('nb-NO')} kr
          </span>
          <ChevronDown size={16} className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="border border-t-0 border-border px-5 py-4 bg-card rounded-b-sm">
          <div className="space-y-2 mb-4">
            <div className="flex justify-between py-1">
              <span className="font-serif text-sm text-foreground/70">Grunnpris</span>
                <span className="font-serif text-sm text-foreground">{(basePrice || steps[0]?.basePrice || 0).toLocaleString('nb-NO')} kr</span>
            </div>
            {lineItems.map((item, i) => (
              <div key={i} className="flex justify-between py-1 border-t border-border/50">
                <div>
                  <span className="font-sans text-[11px] tracking-widest-plus uppercase text-muted-foreground block">{item.label}</span>
                  <span className="font-serif text-base text-foreground">{item.option}</span>
                </div>
                <span className={`font-serif text-sm ${item.price > 0 ? 'text-foreground' : 'text-foreground/60'}`}>
                  {item.price > 0 ? `+${item.price.toLocaleString('nb-NO')}` : item.price.toLocaleString('nb-NO')} kr
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-4">
            <div className="flex justify-between items-end">
              <span className="font-sans text-[11px] tracking-ultra uppercase text-foreground">Totalt estimat</span>
              <div className="flex flex-col items-end">
                <span className="font-serif text-xl text-foreground mb-1">{finalTotalPrice.toLocaleString('nb-NO')} kr</span>
                <p className="font-sans text-[10px] text-muted-foreground">Fastpris inkl MVA 25%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Download specification button */}
      {!submitted && (
        <button
          onClick={downloadPriceBreakdown}
          disabled={downloadLoading}
          className="w-full flex items-center justify-center gap-2 h-11 border border-border text-foreground font-sans text-[11px] tracking-ultra uppercase hover:bg-card disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-sm mb-3"
        >
          <Download size={14} />
          {downloadLoading ? 'Laster ned...' : 'Last ned som PDF'}
        </button>
      )}

      {/* CTA */}
      <div className="mt-6 space-y-3">
        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-3">
            <p className="font-serif text-base text-foreground/70 mb-4">
              Send oss konfigurasjonen din, så tar vi kontakt med et tilbud.
            </p>
            <div className="space-y-3 mb-3">
               <div className="grid grid-cols-2 gap-3">
                 <input
                   required
                   name="firstName"
                   placeholder="Fornavn"
                   className="h-11 px-4 border border-border bg-background font-serif text-base focus:outline-none focus:border-foreground rounded-sm"
                 />
                 <input
                   required
                   type="email"
                   name="email"
                   placeholder="E-post"
                   className="h-11 px-4 border border-border bg-background font-serif text-base focus:outline-none focus:border-foreground rounded-sm"
                 />
                 <input
                   required
                   type="tel"
                   name="phone"
                   placeholder="Telefonnummer"
                   className="h-11 px-4 border border-border bg-background font-serif text-base focus:outline-none focus:border-foreground rounded-sm"
                 />
                 <input
                   required
                   name="postalCode"
                   placeholder="Postnummer"
                   value={formData.postalCode}
                   onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                   className="h-11 px-4 border border-border bg-background font-serif text-base focus:outline-none focus:border-foreground rounded-sm"
                 />
              </div>
              <DeliveryOptions 
                postalCode={formData.postalCode} 
                onDeliveryChange={setDelivery}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-foreground text-background font-sans text-sm tracking-ultra uppercase hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-sm"
            >
              {loading ? 'Sender...' : 'Send konfigurering →'}
            </button>
          </form>
        ) : (
          <div className="border border-border p-8 rounded-sm">
            <div className="text-center mb-4">
              <p className="font-serif text-xl text-foreground mb-1">Takk!</p>
              <p className="font-serif text-sm text-foreground/60">Vi tar kontakt med deg snart med et tilbud.</p>
            </div>
            <button
              onClick={async () => {
                setDownloadLoading(true);
                try {
                  const response = await base44.functions.invoke('generateConfigurationPDF', {
                    firstName: formData.firstName,
                    totalPrice: finalTotalPrice,
                    config,
                    steps,
                    delivery,
                    sauna: steps[0]?.label || 'Sauna Configuration'
                  });
                  
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `LYKKE_Konfigurasjon_${formData.firstName}_${new Date().getTime()}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                } catch (error) {
                  console.error('Error downloading PDF:', error);
                  alert('Feil ved nedlasting av PDF');
                } finally {
                  setDownloadLoading(false);
                }
              }}
              disabled={downloadLoading}
              className="w-full flex items-center justify-center gap-2 h-11 border border-border text-foreground font-sans text-[11px] tracking-ultra uppercase hover:bg-card disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-sm"
            >
              <Download size={14} />
              {downloadLoading ? 'Laster ned...' : 'Last ned som PDF'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}